<script setup lang="ts">
/**
 * AppShell - sidebar, top bar and content area for the company and admin portals.
 *
 * Router-agnostic and string-free: the app passes resolved links, translated
 * labels and which entry is active, and handles `navigate` (see types.ts for
 * why). The layout rules live in shellState.ts.
 *
 * Accessibility:
 * - a skip link to the content, landmarks (the sidebar `nav`, the breadcrumb
 *   `nav`, `main`), `aria-current="page"` on the active entry, and the drawer
 *   announced as a modal dialog while it is open;
 * - in the rail the labels stay in the accessible name (`sr-only`) and appear
 *   as tooltips on hover *and* keyboard focus;
 * - while the drawer or the expanded rail overlays the page, the page is
 *   `inert`, Escape closes the overlay and focus returns to what opened it;
 * - every transition is `motion-safe:` so `prefers-reduced-motion` gets none.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { ChevronsLeft, ChevronsRight, ExternalLink, Menu, X, ChevronRight } from 'lucide-vue-next'
import type { ShellCrumb, ShellLabels, ShellNavGroup, ShellNavItem } from './types'
import {
  SHELL_MEDIUM_QUERY,
  SHELL_WIDE_QUERY,
  isPlainLeftClick,
  readCollapsed,
  shellLayout,
  shellMode,
  writeCollapsed,
  type ShellMode,
} from './shellState'

const props = defineProps<{
  groups: ShellNavGroup[]
  crumbs: ShellCrumb[]
  labels: ShellLabels
  /** localStorage key for the collapsed preference. */
  storageKey: string
  /**
   * Anything that changes on every navigation (the route's full path). The
   * drawer and the expanded rail close when it does, so Back, Forward or a
   * link inside a page never leaves them open over the next page.
   */
  routeKey?: string
}>()

const emit = defineEmits<{
  /** An internal link was followed with a plain click; push it on the router. */
  navigate: [href: string]
}>()

// ---------------------------------------------------------------------------
// Layout state
// ---------------------------------------------------------------------------

function safeStorage(): Storage | undefined {
  try {
    return window.localStorage
  } catch {
    return undefined
  }
}

const mediumQuery = window.matchMedia(SHELL_MEDIUM_QUERY)
const wideQuery = window.matchMedia(SHELL_WIDE_QUERY)
// Read at setup, not on mount, so a phone never paints a frame of the wide layout.
const mode = ref<ShellMode>(shellMode(mediumQuery.matches, wideQuery.matches))
const collapsed = ref(readCollapsed(safeStorage(), props.storageKey))
const peek = ref(false)
const drawerOpen = ref(false)

const layout = computed(() => shellLayout({ mode: mode.value, collapsed: collapsed.value, peek: peek.value, drawerOpen: drawerOpen.value }))
const rail = computed(() => layout.value.rail)

function syncMode(): void {
  const next = shellMode(mediumQuery.matches, wideQuery.matches)
  if (next === mode.value) return
  mode.value = next
  // An overlay belongs to the width it was opened at.
  drawerOpen.value = false
  peek.value = false
}

// ---------------------------------------------------------------------------
// Opening, closing and focus
// ---------------------------------------------------------------------------

const menuButtonRef = ref<HTMLButtonElement | null>(null)
const closeButtonRef = ref<HTMLButtonElement | null>(null)
const toggleRef = ref<HTMLButtonElement | null>(null)

function openDrawer(): void {
  drawerOpen.value = true
  void nextTick(() => closeButtonRef.value?.focus())
}

function closeOverlay(restoreFocus = true): void {
  if (drawerOpen.value) {
    drawerOpen.value = false
    // The menu button sat in the inert page until now; wait for it to wake.
    if (restoreFocus) void nextTick(() => menuButtonRef.value?.focus())
  }
  if (peek.value) {
    peek.value = false
    if (restoreFocus) void nextTick(() => toggleRef.value?.focus())
  }
}

function toggleRail(): void {
  if (mode.value === 'medium') {
    peek.value = !peek.value
    return
  }
  collapsed.value = !collapsed.value
  writeCollapsed(safeStorage(), props.storageKey, collapsed.value)
  tip.value = null
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && layout.value.overlay) {
    event.stopPropagation()
    closeOverlay()
  }
}

/**
 * Close the drawer or expanded rail after a link in it was followed, and put
 * focus on the page. The link is in the sidebar, inert again on a phone; left
 * there, focus would fall to <body> and a keyboard user loses their place.
 */
function closeOverlayToPage(): void {
  if (!layout.value.overlay) return
  closeOverlay(false)
  void nextTick(() => document.getElementById('shell-main')?.focus())
}

// Links the app renders in the slots (RouterLinks) are seen only as a route change.
watch(() => props.routeKey, closeOverlayToPage)

// The page behind an open drawer must not scroll under the person's thumb.
watch(drawerOpen, (open) => {
  document.documentElement.style.overflow = open ? 'hidden' : ''
})

onMounted(() => {
  mediumQuery.addEventListener('change', syncMode)
  wideQuery.addEventListener('change', syncMode)
  document.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  mediumQuery.removeEventListener('change', syncMode)
  wideQuery.removeEventListener('change', syncMode)
  document.removeEventListener('keydown', onKeydown)
  document.documentElement.style.overflow = ''
})

// ---------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------

function follow(event: MouseEvent, href: string, external?: boolean): void {
  if (external || !isPlainLeftClick(event)) return
  event.preventDefault()
  emit('navigate', href)
  tip.value = null
  // Also when the link is the page already open, which changes no route.
  closeOverlayToPage()
}

// ---------------------------------------------------------------------------
// Rail tooltips
//
// One fixed-position tooltip for the whole sidebar. Positioned from the
// trigger's rect rather than with CSS inside the item, because the nav scrolls
// and would clip anything that pokes out of it.
// ---------------------------------------------------------------------------

const tip = ref<{ text: string; top: number; left: number } | null>(null)

function showTip(text: string, event: Event): void {
  if (!rail.value) return
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  tip.value = { text, top: rect.top + rect.height / 2, left: rect.right + 8 }
}

function hideTip(): void {
  tip.value = null
}

function itemClasses(item: ShellNavItem): string[] {
  return [
    'group relative flex items-center gap-2.5 rounded-lg text-sm font-medium outline-none',
    'h-10 md:h-8 motion-safe:transition-colors',
    'focus-visible:ring-2 focus-visible:ring-blue-500/70',
    rail.value ? 'justify-center px-0 w-10 md:w-8 mx-auto' : 'px-2.5',
    item.active
      ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-200'
      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-100',
  ]
}

const contentInset = computed(() => {
  switch (layout.value.inset) {
    case 'rail': return 'md:pl-16'
    case 'full': return 'md:pl-16 lg:pl-60'
    default: return ''
  }
})

const asideWidth = computed(() => {
  if (mode.value === 'mobile') return 'w-72 max-w-[85vw]'
  return rail.value ? 'w-16' : 'w-60'
})

function skipToContent(): void {
  document.getElementById('shell-main')?.focus()
}

/** Exposed to the brand and footer slots, so their own controls behave like nav items. */
const slotProps = computed(() => ({ collapsed: rail.value, showTip, hideTip }))
</script>

<template>
  <div class="min-h-dvh bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
    <!-- Focused by hand: under hash history "#shell-main" is a route, and
         the catch-all would send the person to the dashboard instead. -->
    <a
      href="#shell-main"
      :inert="layout.overlay ? true : undefined"
      @click.prevent="skipToContent"
      class="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[70] focus:rounded-lg focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-blue-700 focus:shadow-lg dark:focus:bg-gray-800 dark:focus:text-blue-300"
    >{{ labels.skipToContent }}</a>

    <!-- Backdrop for the drawer (phones) and the expanded rail (medium). -->
    <Transition
      enter-active-class="motion-safe:transition-opacity motion-safe:duration-200"
      leave-active-class="motion-safe:transition-opacity motion-safe:duration-150"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="layout.overlay"
        class="fixed inset-0 z-40 bg-gray-950/40 dark:bg-black/60"
        aria-hidden="true"
        @click="closeOverlay()"
      />
    </Transition>

    <!-- Not a landmark itself (the <nav> inside is); while it is a drawer over
         the page it is a modal dialog. -->
    <div
      id="shell-sidebar"
      :role="mode === 'mobile' && drawerOpen ? 'dialog' : undefined"
      :aria-modal="mode === 'mobile' && drawerOpen ? 'true' : undefined"
      :aria-label="mode === 'mobile' && drawerOpen ? labels.navigation : undefined"
      :inert="mode === 'mobile' && !drawerOpen ? true : undefined"
      :class="[
        'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-gray-200/80 bg-white dark:border-white/10 dark:bg-gray-950',
        'motion-safe:transition-[width,transform,box-shadow] motion-safe:duration-200 motion-safe:ease-out',
        asideWidth,
        mode === 'mobile' && !drawerOpen ? '-translate-x-full' : 'translate-x-0',
        layout.overlay ? 'shadow-2xl' : '',
      ]"
    >
      <!-- Brand -->
      <div :class="['flex h-14 shrink-0 items-center gap-2', rail ? 'justify-center px-2' : 'px-3']">
        <div class="min-w-0 flex-1">
          <slot name="brand" v-bind="slotProps" />
        </div>
        <button
          v-if="mode === 'mobile'"
          ref="closeButtonRef"
          type="button"
          class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/70 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-100"
          :aria-label="labels.closeMenu"
          @click="closeOverlay()"
        >
          <X :size="18" aria-hidden="true" />
        </button>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 overflow-y-auto overflow-x-hidden px-3 pb-3 pt-1" :aria-label="labels.navigation" @scroll="hideTip">
        <div v-for="(group, index) in groups" :key="group.id" :class="index > 0 ? 'mt-5' : ''">
          <template v-if="group.items.length || group.pending">
            <!-- A label for the list, not a heading: the page's own h1 comes
                 after the sidebar, and h2s here would precede it. -->
            <p
              v-if="group.label && !rail"
              :id="`shell-group-${group.id}`"
              class="mb-1 px-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
            >{{ group.label }}</p>
            <div v-else-if="index > 0 && rail" class="mx-auto mb-2 h-px w-6 bg-gray-200 dark:bg-white/10" aria-hidden="true" />
            <ul
              class="space-y-0.5"
              role="list"
              :aria-labelledby="group.label && !rail ? `shell-group-${group.id}` : undefined"
              :aria-label="group.label && rail ? group.label : undefined"
            >
              <li v-for="item in group.items" :key="item.id">
                <a
                  :href="item.href"
                  :target="item.external ? '_blank' : undefined"
                  :rel="item.external ? 'noopener noreferrer' : undefined"
                  :aria-current="item.active ? 'page' : undefined"
                  :class="itemClasses(item)"
                  @click="follow($event, item.href, item.external)"
                  @mouseenter="showTip(item.label, $event)"
                  @mouseleave="hideTip"
                  @focus="showTip(item.label, $event)"
                  @blur="hideTip"
                >
                  <component
                    :is="item.icon"
                    :size="17"
                    :stroke-width="item.active ? 2.25 : 1.9"
                    aria-hidden="true"
                    :class="[
                      'shrink-0 motion-safe:transition-colors',
                      item.active ? 'text-blue-600 dark:text-blue-300' : 'text-gray-500 group-hover:text-gray-700 dark:text-gray-500 dark:group-hover:text-gray-300',
                    ]"
                  />
                  <span :class="rail ? 'sr-only' : 'min-w-0 flex-1 truncate'">{{ item.label }}</span>
                  <ExternalLink v-if="item.external && !rail" :size="13" class="shrink-0 text-gray-400" aria-hidden="true" />
                  <span
                    v-if="item.badge && !rail"
                    class="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-gray-100 px-1.5 text-[11px] font-semibold tabular-nums text-gray-700 dark:bg-white/10 dark:text-gray-200"
                  >{{ item.badge > 99 ? '99+' : item.badge }}</span>
                  <span
                    v-if="item.dot && !rail"
                    class="ml-auto h-2 w-2 shrink-0 rounded-full bg-red-500"
                    aria-hidden="true"
                  />
                  <span
                    v-if="rail && (item.badge || item.dot)"
                    class="absolute right-1 top-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-gray-950"
                    aria-hidden="true"
                  />
                </a>
              </li>
              <li v-for="n in group.pending ?? 0" :key="`pending-${n}`" aria-hidden="true">
                <div :class="['flex h-10 items-center gap-2.5 md:h-8', rail ? 'justify-center' : 'px-2.5']">
                  <div class="h-4 w-4 shrink-0 animate-pulse motion-reduce:animate-none rounded bg-gray-200 dark:bg-white/10" />
                  <div v-if="!rail" class="h-3 flex-1 animate-pulse motion-reduce:animate-none rounded bg-gray-200 dark:bg-white/10" :style="{ maxWidth: `${60 + ((n * 23) % 30)}%` }" />
                </div>
              </li>
            </ul>
          </template>
        </div>
      </nav>

      <!-- Footer: usage, the person, and the collapse control. -->
      <div class="shrink-0 space-y-2 border-t border-gray-200/80 p-3 dark:border-white/10">
        <slot name="sidebar-footer" v-bind="slotProps" />
        <button
          v-if="mode !== 'mobile'"
          ref="toggleRef"
          type="button"
          :class="[
            'flex h-8 items-center gap-2 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/70 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-100 motion-safe:transition-colors',
            rail ? 'mx-auto w-8 justify-center' : 'w-full px-2.5',
          ]"
          aria-controls="shell-sidebar"
          :aria-expanded="!rail"
          :aria-label="rail ? labels.expand : labels.collapse"
          @click="toggleRail"
          @mouseenter="showTip(labels.expand, $event)"
          @mouseleave="hideTip"
          @focus="showTip(labels.expand, $event)"
          @blur="hideTip"
        >
          <ChevronsRight v-if="rail" :size="16" aria-hidden="true" />
          <template v-else>
            <ChevronsLeft :size="16" aria-hidden="true" />
            <span>{{ labels.collapse }}</span>
          </template>
        </button>
      </div>
    </div>

    <!-- Page -->
    <div
      :inert="layout.overlay ? true : undefined"
      :class="['flex min-h-dvh flex-col motion-safe:transition-[padding] motion-safe:duration-200 motion-safe:ease-out', contentInset]"
    >
      <header class="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-gray-200/80 bg-gray-50/85 px-3 backdrop-blur-md supports-[backdrop-filter]:bg-gray-50/70 sm:px-4 lg:px-6 dark:border-white/10 dark:bg-gray-900/85 dark:supports-[backdrop-filter]:bg-gray-900/70">
        <button
          v-if="mode === 'mobile'"
          ref="menuButtonRef"
          type="button"
          data-shell-menu
          class="relative -ml-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-200/60 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/70 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white"
          :aria-label="labels.openMenu"
          aria-controls="shell-sidebar"
          :aria-expanded="drawerOpen"
          @click="openDrawer"
        >
          <Menu :size="20" aria-hidden="true" />
          <span
            v-if="groups.some(g => g.items.some(i => i.badge || i.dot))"
            class="absolute right-2 top-2 h-2 w-2 rounded-full bg-red-500 ring-2 ring-gray-50 dark:ring-gray-900"
            aria-hidden="true"
          />
        </button>

        <nav :aria-label="labels.breadcrumb" class="min-w-0 flex-1">
          <ol class="flex min-w-0 items-center gap-1.5 text-sm">
            <li
              v-for="(crumb, index) in crumbs"
              :key="index"
              :class="['flex min-w-0 items-center gap-1.5', index < crumbs.length - 1 ? 'hidden sm:flex' : '']"
            >
              <ChevronRight v-if="index > 0" :size="14" class="hidden shrink-0 text-gray-300 sm:block dark:text-gray-600" aria-hidden="true" />
              <a
                v-if="crumb.href && index < crumbs.length - 1"
                :href="crumb.href"
                class="truncate rounded text-gray-500 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/70 dark:text-gray-400 dark:hover:text-gray-100"
                @click="follow($event, crumb.href)"
              >{{ crumb.label }}</a>
              <span
                v-else
                class="truncate font-semibold text-gray-900 dark:text-gray-100"
                :aria-current="index === crumbs.length - 1 ? 'page' : undefined"
              >{{ crumb.label }}</span>
            </li>
          </ol>
        </nav>

        <!-- Pages put their own buttons here through ShellPageActions. -->
        <div id="shell-page-actions" class="flex shrink-0 items-center gap-2" />
        <slot name="topbar-end" />
      </header>

      <main id="shell-main" tabindex="-1" class="flex-1 focus:outline-none">
        <slot />
      </main>
    </div>

    <!-- Rail tooltip -->
    <div
      v-if="tip"
      role="tooltip"
      class="pointer-events-none fixed z-[60] -translate-y-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-lg dark:bg-gray-100 dark:text-gray-900"
      :style="{ top: `${tip.top}px`, left: `${tip.left}px` }"
    >{{ tip.text }}</div>
  </div>
</template>
